import React, { useEffect, useId, useRef, useState } from 'react';
import { PAYPAL_CLIENT_ID, PAYPAL_HOSTED_BUTTON_ID } from '../../utils/constants';

declare global {
  interface Window {
    paypal?: {
      HostedButtons: (config: { hostedButtonId: string }) => {
        render: (selector: string) => Promise<void>;
      };
    };
  }
}

const SDK_ID = 'migoyugo-paypal-sdk';

function loadPayPalSdk(): Promise<void> {
  if (window.paypal?.HostedButtons) {
    return Promise.resolve();
  }

  const existing = document.getElementById(SDK_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('PayPal SDK failed to load')), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SDK_ID;
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(PAYPAL_CLIENT_ID)}&components=hosted-buttons&disable-funding=venmo&currency=CAD`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('PayPal SDK failed to load'));
    document.body.appendChild(script);
  });
}

const PayPalHostedButton: React.FC = () => {
  const reactId = useId();
  const containerId = `paypal-container-${reactId.replace(/:/g, '')}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;

    loadPayPalSdk()
      .then(async () => {
        if (cancelled || !containerRef.current || !window.paypal?.HostedButtons) return;
        containerRef.current.innerHTML = '';
        await window.paypal.HostedButtons({
          hostedButtonId: PAYPAL_HOSTED_BUTTON_ID,
        }).render(`#${containerId}`);
        if (!cancelled) setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [containerId]);

  return (
    <div className="shop-paypal-wrap">
      {status === 'loading' && <p className="shop-paypal-status">Loading PayPal checkout…</p>}
      {status === 'error' && (
        <p className="shop-paypal-error">
          PayPal could not be loaded. Please refresh the page or try again later.
        </p>
      )}
      <div id={containerId} ref={containerRef} />
    </div>
  );
};

export default PayPalHostedButton;
